// NOTE: this file has all of the models and types that the BE will expect from payloads, and return in responses
export type DmOperator =
  | 'GT'
  | 'LT'
  | 'LE'
  | 'GE'
  | 'EQ'
  | 'NEQ'
  | 'IN'
  | 'NOT_IN'
  | 'IS'
  | 'ISNT'
  | 'IS_EMPTY'
  | 'IS_NOT_EMPTY'
  | 'NONE_IS_EMPTY'
  | 'ANY_IS_EMPTY'
  | 'LIKE'
  | 'NOT_LIKE'
  | 'ILIKE'
  | 'NOT_ILIKE'
  | 'BETWEEN'
  | 'AND'
  | 'OR'
  | 'NOT'
  | 'ADD'
  | 'SUB'
  | 'MUL'
  | 'DIV'
  | 'ANY_IN'
  | 'NONE_IN'
  | 'ANY_EQ'
  | 'NONE_EQ'
  | 'ANY_LIKE'
  | 'NONE_LIKE'
  | 'CONTAINS'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'LOWERCASE'
  | 'UPPERCASE'
  | 'TRIM'
  | 'CAPITALIZE'
  | 'ABS'
  | 'SQRT'
  | 'EXP'
  | 'MODULO'
  | 'LN'
  | 'LOG'
  | 'SANITIZE'
  | 'LEVENSHTEIN_DIVIDED'
  | 'NOT_STARTS_WITH'
  | 'NOT_ENDS_WITH'
  | 'ILIKE_CONTAINS'
  | 'NOT_ILIKE_CONTAINS'
  | 'FUZZY_SCORE'
  | 'AI_FUZZY_SCORE';

export type CompositeOperator = 'AND' | 'OR';

export enum DmDurationUnits {
  MINUTE = 'MINUTE',
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

export enum ValueTypes {
  ACH_FRAUD_FIELD = 'ACH_FRAUD_FIELD',
  CHECK_FRAUD_FIELD = 'CHECK_FRAUD_FIELD',
  CONSORTIUM_FIELD = 'CONSORTIUM_FIELD',
  CONSTANT = 'CONSTANT',
  DURATION = 'DURATION',
  FIELD = 'FIELD',
  FLAG_FIELD = 'FLAG_FIELD',
  INTERVAL = 'INTERVAL',
  MATCHLIST_FIELD = 'MATCHLIST_FIELD',
  MATCHLIST = 'MATCHLIST',
  NULL = 'NULL',
  TAG_FIELD = 'TAG_FIELD',
  TAG = 'TAG',
  THRESHOLD = 'THRESHOLD',
  VARIABLE = 'VARIABLE',
}

export interface DurationValue {
  type: ValueTypes.DURATION;
  value: number;
  unit: DmDurationUnits;
}

export interface IntervalValue {
  type: ValueTypes.INTERVAL;
  value: number;
  unit: DmDurationUnits;
}

export type DmObjectToFlag =
  | 'ENTITY'
  | 'INSTRUMENT'
  | 'DEVICE'
  | 'TXN_EVENT'
  | 'ACTION_EVENT';

export enum DmSenderReceivers {
  SENDER = 'SENDER',
  RECEIVER = 'RECEIVER',
  SENDER_OR_RECEIVER = 'SENDER_OR_RECEIVER',
}
export type DmSenderReceiver =
  | DmSenderReceivers.SENDER
  | DmSenderReceivers.RECEIVER
  | DmSenderReceivers.SENDER_OR_RECEIVER;

export interface NullValue {
  type: ValueTypes.NULL;
  value?: null;
}

export interface ConstantValue {
  type: ValueTypes.CONSTANT;
  value: string | number | boolean | (string | number)[] | Date;
}

export interface FieldToFlagValue {
  type: ValueTypes.FLAG_FIELD;
  field: string;
  model: string;
  values: string[];
}

export interface FieldValue {
  type: ValueTypes.FIELD;
  field: string;
  label?: string;
  model: string;
  datatype: string;
  cardinality?: 'one' | 'many';
}

export interface MatchlistFieldValue {
  type: ValueTypes.MATCHLIST_FIELD;
  matchlist_type: string;
  matchlist_id: number;
}

export type DmMatchlistInOperator = 'IN' | 'NOT_IN' | 'ANY_IN' | 'NONE_IN';

export type DmMatchlistStringOperator =
  | 'STARTS_WITH'
  | 'NOT_STARTS_WITH'
  | 'ENDS_WITH'
  | 'NOT_ENDS_WITH'
  | 'ILIKE_CONTAINS'
  | 'NOT_ILIKE_CONTAINS';

export type DmMatchlistOperator = DmOperator &
  (DmMatchlistInOperator | DmMatchlistStringOperator);

export interface MatchlistValue {
  type: ValueTypes.MATCHLIST;
  matchlist_id: number;
  matchlist_name: string;
  operator: DmMatchlistOperator;
  field: FieldValue;
}

export enum TagTypes {
  INCLUSION = 'INCLUSION',
  EXCLUSION = 'EXCLUSION',
}

export enum TagOperators {
  AND = 'AND',
  OR = 'OR',
}

export interface TagField {
  type: ValueTypes.TAG_FIELD;
  tag_name: string;
}

export interface TagValue {
  type: ValueTypes.TAG;
  tag_operator: TagOperators;
  tag_type: TagTypes;
  tags_fields: TagField[];
}

export type VariableValue =
  | {
      type: ValueTypes.VARIABLE;
      name: string;
      value?:
        | VariableValue
        | FieldValue
        | ConstantValue
        | DurationValue
        | IntervalValue
        | TagValue;
    }
  | {
      type: ValueTypes.VARIABLE;
      name: string;
      value?: string;
    };

export interface ConsortiumField {
  type: ValueTypes.CONSORTIUM_FIELD;
  field: string;
  label?: string;
  model: string;
  subtype: 'blocked' | 'blocked_reasons' | 'last_blocked_at';
}

export interface AlertCondition {
  type: 'ALERT_CONDITION';
  operator: 'ANY_IN' | 'NONE_IN';
  alert_field: string;
  right_side_value: QueryTreeNode;
}

export interface CheckFraudField {
  type: ValueTypes.CHECK_FRAUD_FIELD;
  subtype: 'duplicate_serial_number' | 'missing_serial_number';
  txn_check_types: string[] | null;
  lookback: DurationValue | null;
}

export interface AchFraudField {
  type: ValueTypes.ACH_FRAUD_FIELD;
  subtype: 'duplicate_ach_transaction';
  txn_ach_types: string[] | null;
}

export interface ThresholdValue {
  type: ValueTypes.THRESHOLD;
  value: number;
  reference_column: QueryTreeNode;
}

export enum RtrTypes {
  BASIC = 'BASIC',
  VELOCITY = 'VELOCITY',
  WATCHLIST = 'WATCHLIST',
}

export enum UiInputs {
  DROPDOWN = 'dropdown',
  PLAIN_TEXT = 'plain_text',
}

export interface MetaUi {
  input_choice?: UiInputs;
  ui_operator?: DmOperator;
  rtr_ui_subtype?: RtrTypes;
}

export enum CompositeTypes {
  COMPOSITE = 'COMPOSITE',
  RULE_CONDITION_COMPOSITE = 'rule_condition_composite',
}

export interface CompositeQueryTreeNode {
  type: CompositeTypes;
  operator: DmOperator;
  operands: QueryTreeNode[];
  meta_ui?: MetaUi;
}

export type QueryTreeNode =
  | null
  | ConstantValue
  | NullValue
  | FieldValue
  | {
      type: 'fact_id';
      value: string;
    }
  | CompositeQueryTreeNode
  | DurationValue
  | MatchlistValue
  | MatchlistFieldValue
  | IntervalValue
  | TagValue
  | VariableValue
  | ConsortiumField
  | AlertCondition
  | CheckFraudField
  | AchFraudField
  | ThresholdValue;

export const enum DmAggregateFunctions {
  SUM = 'SUM',
  COUNT = 'COUNT',
  AVERAGE = 'AVERAGE',
  MAXIMUM = 'MAXIMUM',
  MINIMUM = 'MINIMUM',
}

// these are based on pensieve/models/event.py
export const enum EventTypes {
  // Transactions involving monetary value
  TXN = 'TXN',
  // User-triggered actions e.g. login, logoff, access
  ACTION = 'ACTION',
  // Internally(customer)-triggered activity e.g. manual investigations, passed verification
  INTERNAL = 'INTERNAL',
}

// TODO: rename this to VariableSubType, since it's not just events
export type DmEventSubtype =
  | 'ACTION_EVENT'
  | 'TXN_EVENT'
  | 'FIELD'
  | 'CONSORTIUM';

export type ConsortiumFactObject = 'consortium_alert';

export type DmObjectSubtype =
  | 'USER'
  | 'BUSINESS'
  | 'ACTION_EVENT'
  | 'TXN_EVENT'
  | null;

export enum DmFactTypes {
  CONSORTIUM_FACT = 'CONSORTIUM_FACT',
  ENTITY_VALUE_FACT = 'ENTITY_VALUE_FACT',
  EVENT_BY_OBJECT_FACT = 'EVENT_BY_OBJECT_FACT',
  MULTIPLE_PERSPECTIVES_AGGREGATION = 'MULTIPLE_PERSPECTIVES_AGGREGATION',
  OBJECT_FACT = 'OBJECT_FACT',
}

export enum FeDmFactTypes {
  EVENT_BY_OBJECT_TRANSACTION_FACT = 'EVENT_BY_OBJECT_TRANSACTION_FACT',
  EVENT_BY_OBJECT_ACTION_FACT = 'EVENT_BY_OBJECT_ACTION_FACT',
}

export type FeDmEventByObjectFactType = DmFactTypes | FeDmFactTypes;

export type DmEventByObjectFactType =
  | DmFactTypes.EVENT_BY_OBJECT_FACT
  | DmFactTypes.MULTIPLE_PERSPECTIVES_AGGREGATION;

export interface DmFactBase {
  type: DmFactTypes;
  id: string;
  object: DmObjectToFlag;
}

export interface DmObjectFact extends DmFactBase {
  type: DmFactTypes.OBJECT_FACT;
  object_subtype: DmObjectSubtype;
  value: QueryTreeNode; // NOTE: this is the same as `event_filters` for an event by object fact
}

export interface DmEntityValueFact extends DmFactBase {
  type: DmFactTypes.ENTITY_VALUE_FACT;
  field: FieldValue;
  value: QueryTreeNode; // NOTE: this is the same as `event_filters` for an event by object fact
}

export interface DmEventByObjectFact extends DmFactBase {
  type: DmEventByObjectFactType;
  sender_receiver?: DmSenderReceiver;
  event_filters: QueryTreeNode;
  aggregate_function: DmAggregateFunctions;
  aggregate_field: string | FieldValue;
  event_subtype: DmEventSubtype;
  window_end_offset: DurationValue;
  window_start_offset: DurationValue;
  window_field?: FieldValue;
}

export interface DmConsortiumFact extends DmFactBase {
  type: DmFactTypes.CONSORTIUM_FACT;
  consortium_object: ConsortiumFactObject;
  aggregate_function: DmAggregateFunctions;
  aggregate_field: FieldValue;
  window_end_offset: DurationValue;
  window_start_offset: DurationValue;
  window_field?: FieldValue;
  object_filters: QueryTreeNode; // NOTE: this is the same as `event_filters` for an event by object fact
}

export interface MultiplePerspectivesAggregation extends DmEventByObjectFact {
  type: DmFactTypes.MULTIPLE_PERSPECTIVES_AGGREGATION;
  perspectives: FieldValue[];
}

export interface RtrEventByObjectFact extends DmEventByObjectFact {
  include_current_evaluated_event: boolean;
  incoming_event_perspectives?: DmSenderReceiver;
  historical_event_perspectives?: DmSenderReceiver;
}

export type DmFact =
  | MultiplePerspectivesAggregation
  | DmEventByObjectFact
  | RtrEventByObjectFact
  | DmObjectFact
  | DmEntityValueFact
  | DmConsortiumFact;

export interface IRSchema {
  type?: null;
  rtr_type?: RtrTypes;
  facts: DmFact[];
  object: DmObjectToFlag;
  field_to_flag?: FieldToFlagValue;
  rule_condition: CompositeQueryTreeNode | null;
}
